'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Map,
  BookOpen,
  BarChart3,
  Wrench,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  shortcut: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, shortcut: 'D' },
  { id: 'nav-roadmap', label: 'Roadmap', href: '/roadmap-view', icon: Map, shortcut: 'R' },
  { id: 'nav-thisweek', label: 'This Week', href: '/week-detail-view', icon: BookOpen, shortcut: 'T' },
  { id: 'nav-progress', label: 'Progress', href: '/dashboard#progress', icon: BarChart3, shortcut: '' },
  { id: 'nav-tools', label: 'Study Tools', href: '/dashboard#tools', icon: Wrench, shortcut: '' },
  { id: 'nav-settings', label: 'Settings', href: '/dashboard#settings', icon: Settings, shortcut: '' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  currentPath: string;
}

export default function Sidebar({ collapsed, onToggleCollapse, currentPath }: SidebarProps) {
  return (
    <aside
      style={{
        width: '100%',
        height: '100vh',
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-card)',
        borderRight: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: collapsed ? '20px 12px' : '20px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: '64px',
        }}
      >
        <AppLogo size={28} />
        {!collapsed && (
          <span
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '17px',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            LearnPath
          </span>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = currentPath === item.href || (item.href !== '/dashboard' && currentPath.startsWith(item.href.split('#')[0]));
          return (
            <Link
              key={item.id}
              href={item.href}
              title={collapsed ? `${item.label}${item.shortcut ? ` (${item.shortcut})` : ''}` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: collapsed ? '10px 12px' : '10px 12px',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 150ms ease',
                backgroundColor: isActive ? 'var(--accent-glow)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
                border: isActive ? '1px solid var(--accent-border)' : '1px solid transparent',
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-elevated)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                }
              }}
            >
              <Icon size={18} />
              {!collapsed && (
                <span style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {item.label}
                </span>
              )}
              {!collapsed && item.shortcut && (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '11px',
                    fontFamily: 'IBM Plex Mono, monospace',
                    color: 'var(--text-muted)',
                    backgroundColor: 'var(--bg-elevated)',
                    padding: '1px 5px',
                    borderRadius: '4px',
                    border: '1px solid var(--border)',
                  }}
                >
                  {item.shortcut}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border)' }}>
        <button
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-end',
            padding: '8px 12px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'transparent',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-elevated)';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}